import React, { useState, useContext, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import PromenaLozinke from './KorisnikTabs/PromenaLozinke';
import MobilnaAplikacija from './KorisnikTabs/MobilnaAplikacija';
import { LANGS, setAppLanguage } from '../../../i18n';
import { AuthContext } from '../../../Header/auth - context';
import { applyThemeWithReveal, jeRetro, jeTamna } from '../../../theme';
import TemaPrekidac from '../../../custom/TemaPrekidac';
import RetroPrekidac from '../../../custom/RetroPrekidac';

const KorisnikHome = () => {
    const { t, i18n } = useTranslation();
    const auth = useContext(AuthContext);
    const dispatch = useDispatch();
    const toast = useRef(null);

    const [changeView, setChangeView] = useState(1);
    const [jezik, setJezik] = useState(i18n.language || 'sr');
    const themeStore = useSelector(s => s.theme || 'light');
    const [tema, setTema] = useState(themeStore);
    /* Dva prekidača pokrivaju četiri teme, i to po dve nezavisne osi:
         dan/noć  → svetlo ili tamno
         retro    → običan izgled ili pikselski
       Presek daje: light, dark, retro (svetli), retrodark. Zato se tema ne
       bira spiskom nego se SASTAVLJA iz dva stanja — tako prelaz iz tamne u
       retro ostaje taman, što je i očekivano. */
    const dark = jeTamna(tema);
    const retro = jeRetro(tema);
    const sastavi = (jeTamno, jeRetro) =>
        jeRetro ? (jeTamno ? 'retrodark' : 'retro') : (jeTamno ? 'dark' : 'light');
    const activeClass = 'activeButton';

    const handleJezik = (code) => {
        setJezik(code);
        setAppLanguage(code); // odmah promeni prikaz + zapamti lokalno
        fetch(process.env.REACT_APP_BACKEND_URL + '/users/language', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ jezik: code }),
        })
            .then(r => { if (!r.ok) throw r; return r.json(); })
            .then(() => toast.current?.show({ severity: 'success', summary: i18n.getFixedT(code)('korisnik.jezikSacuvan'), life: 2500 }))
            .catch(() => {});
    };

    const handleTema = (novaTema, event) => {
        setTema(novaTema);
        const tema = novaTema;
        /* Tačka iz koje se nova tema otkriva krugom — sredina samog prekidača,
           da prelaz kreće odatle gde je korisnik kliknuo. */
        const okvir = event?.currentTarget?.getBoundingClientRect();
        const izvor = okvir ? { x: okvir.left + okvir.width / 2, y: okvir.top + okvir.height / 2 } : null;
        applyThemeWithReveal(tema, izvor);            // odmah primeni na celu aplikaciju (+ localStorage)
        dispatch({ type: 'THEME_SET', value: tema }); // reaktivno (chat i ostalo prati)
        fetch(process.env.REACT_APP_BACKEND_URL + '/users/theme', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ tema }),
        })
            .then(r => { if (!r.ok) throw r; return r.json(); })
            .then(() => toast.current?.show({ severity: 'success', summary: t('korisnik.temaSacuvana'), life: 2500 }))
            .catch(() => {});
    };

    const sectionTitle = { fontWeight: 700, fontSize: '1.05rem', margin: '0 0 6px' };
    const hr = { border: 'none', borderTop: '1px solid var(--border, #d0d0d0)', margin: '4px 0 18px' };

    return (
        <div style={{ marginTop: '1%' }}>
            <Toast ref={toast} />

            <Button id={changeView === 1 ? activeClass : undefined} label={t('korisnik.promenaLozinke')} className="mt-3 zaposleniShift adminTab" onClick={() => setChangeView(1)} />
            <Button id={changeView === 2 ? activeClass : undefined} label={t('korisnik.podesavanja')} className="mt-3 zaposleniShift adminTab" onClick={() => setChangeView(2)} />
            <Button id={changeView === 3 ? activeClass : undefined} label={t('korisnik.mobilnaAplikacija')} className="mt-3 zaposleniShift adminTab" onClick={() => setChangeView(3)} />

            {changeView === 1 && <PromenaLozinke />}
            {changeView === 3 && <MobilnaAplikacija />}

            {changeView === 2 && (
                <div style={{ maxWidth: 460, margin: '1.8rem auto 0', padding: '0 16px', display: 'flex', flexDirection: 'column' }}>
                    {/* ── Jezik ── */}
                    <h3 style={sectionTitle}>{t('korisnik.jezikAplikacije')}</h3>
                    <hr style={hr} />
                    <Dropdown
                        value={jezik}
                        options={LANGS}
                        optionLabel="label"
                        optionValue="code"
                        onChange={(e) => handleJezik(e.value)}
                        style={{ width: '100%', maxWidth: 300 }}
                    />

                    {/* ── Tema ── */}
                    <h3 style={{ ...sectionTitle, marginTop: '30px' }}>{t('korisnik.tema')}</h3>
                    <hr style={hr} />
                    {/* Dva prekidača, tri teme. Dan/noć bira između svetle i tamne i
                        uvek izlazi iz retroa; retro prekidač ulazi u retro, a gašenjem
                        vraća onu temu sa koje se krenulo. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        {/* Isti prekidač kao u podešavanjima chata — custom/TemaPrekidac.js */}
                        <TemaPrekidac dark={dark} onToggle={(e) => handleTema(sastavi(!dark, retro), e)} ariaLabel={t('korisnik.tema')} />
                        {/* Natpis govori šta OVAJ prekidač nosi (svetlo/tamno), a ne koja
                            je tema trenutno aktivna — inače bi u retro temi oba reda
                            pisala „Retro mod" i ne bi se videlo šta koji radi. */}
                        <span className="tema-natpis">
                            {dark ? t('korisnik.tamniMod') : t('korisnik.svetliMod')}
                        </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '14px' }}>
                        <RetroPrekidac
                            on={retro}
                            onToggle={(e) => handleTema(sastavi(dark, !retro), e)}
                            ariaLabel={t('korisnik.retroMod')}
                        />
                        <span className="tema-natpis">
                            {retro ? (dark ? t('korisnik.retroModTamni') : t('korisnik.retroModSvetli')) : t('korisnik.retroMod')}
                            <small className="tema-opis">{t('korisnik.retroOpis')}</small>
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KorisnikHome;
